import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { CartItem } from './CartContext';
import { logger } from '../utils/logger';
import { auth, googleProvider, db } from '../config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  signOut,
  updateProfile as fbUpdateProfile,
  signInWithRedirect,
  getRedirectResult,
  updateEmail as fbUpdateEmail,
} from 'firebase/auth';
import { COLLECTIONS } from '../config/constants';

// Firebase Auth tabanlı kimlik. Adres / hızlı sipariş gibi profil verileri
// localStorage'da cache'lenir (PROFILES anahtarı altında, uid'ye bağlı).

export interface User {
  uid: string;
  email: string;
  displayName: string;
  createdAt: string;
  role?: 'admin' | 'customer';
}

export interface Address {
  id: string;
  title: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  district: string;
  city: string;
  postalCode: string;
  isDefault: boolean;
}

export interface QuickOrderTemplate {
  id: string;
  name: string;
  items: (Omit<CartItem, 'quantity'> & { quantity: number })[];
  addressId: string;
  paymentMethod: 'cash_on_delivery' | 'credit_card';
  notes?: string;
  isFavorite?: boolean;
  createdAt: string;
}

interface UserProfile {
  addresses: Address[];
  quickOrders: QuickOrderTemplate[];
  lastUsedAddressId?: string;
  consents?: {
    terms?: { accepted: boolean; date: string };
    kvkk?: { accepted: boolean; date: string };
    marketing?: { accepted: boolean; date: string };
  };
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (
    email: string,
    password: string,
    profileData?: Partial<Pick<User, 'displayName'>> & { marketingConsent?: boolean; kvkkConsent?: boolean; termsConsent?: boolean }
  ) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<{ success: boolean; error?: string }>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  // Addresses
  getAddresses: () => Address[];
  addAddress: (address: Omit<Address, 'id' | 'isDefault'> & { isDefault?: boolean }) => Address;
  updateAddress: (address: Address) => void;
  deleteAddress: (id: string) => void;
  setDefaultAddress: (id: string) => void;
  // Quick orders
  getQuickOrders: () => QuickOrderTemplate[];
  saveQuickOrder: (template: Omit<QuickOrderTemplate, 'id' | 'createdAt'>) => QuickOrderTemplate;
  deleteQuickOrder: (id: string) => void;
  setFavoriteQuickOrder: (id: string) => void;
  setLastUsedAddress: (id: string) => void;
  getLastUsedAddress: () => string | undefined;
  // Profile edits
  updateEmail: (newEmail: string) => Promise<{ success: boolean; error?: string }>;
  canEditEmail: boolean;
}

const STORAGE = {
  PROFILES: 'local-auth-profiles',
} as const;

// Eski local-auth modundan kalmış olabilecek anahtarları temizle (bir kerelik).
const LEGACY_KEYS = ['local-auth-users', 'local-auth-current-user'];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps { children: ReactNode; }

const readProfiles = (): Record<string, UserProfile> => {
  try { return JSON.parse(localStorage.getItem(STORAGE.PROFILES) || '{}'); } catch { return {}; }
};
const writeProfiles = (profiles: Record<string, UserProfile>) => localStorage.setItem(STORAGE.PROFILES, JSON.stringify(profiles));

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Geçiş döneminde rol fallback'i için tutuluyor; custom claim yaygınlaşınca silinecek.
  const ADMIN_EMAIL = (process.env.REACT_APP_ADMIN_EMAIL || 'agorasuisletme@gmail.com').toLowerCase();

  // Firestore'a kullanıcı dökümanı yaz (rol dahil)
  const ensureUserDoc = async (uid: string, email: string, role: 'admin'|'customer', createdAt?: string) => {
    try {
      await setDoc(doc(db, COLLECTIONS.USERS, uid), {
        uid,
        email: email.toLowerCase(),
        role,
        createdAt: createdAt || new Date().toISOString(),
      }, { merge: true });
    } catch (err) {
      logger.warn('ensureUserDoc başarısız:', err);
    }
  };

  // Rol belirleme — öncelik sırası:
  // 1) Custom claim (admin: true)         ← yeni güvenli yöntem
  // 2) ADMIN_EMAIL eşleşmesi              ← geçiş döneminde stale Firestore'u override eder
  // 3) Firestore users/{uid}.role         ← son çare (claim yaygınlaşınca tek doğru kaynak)
  //
  // Not: Geçiş döneminde Firestore'da role='customer' kalmış olabilecek admin
  // hesapları için ADMIN_EMAIL match'i 2. önceliğe çekildi. ensureUserDoc bir
  // sonraki yazımda Firestore'u 'admin'e güncelleyecek (heal-on-write).
  const determineRole = async (
    fbUser: { uid: string; email: string | null; getIdTokenResult?: (force?: boolean) => Promise<{ claims: Record<string, unknown> }> }
  ): Promise<'admin' | 'customer'> => {
    const email = (fbUser.email || '').toLowerCase();

    try {
      if (typeof fbUser.getIdTokenResult === 'function') {
        // force=true: claim eklendiğinde/iptal edildiğinde 1 saatlik token cache'ini
        // beklemeden güncel yetkiyi al.
        const tokenResult = await fbUser.getIdTokenResult(true);
        if (tokenResult?.claims?.admin === true) return 'admin';
      }
    } catch (err) {
      logger.warn('Custom claim okunamadı, fallback:', err);
    }

    if (email && email === ADMIN_EMAIL) return 'admin';

    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, fbUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data?.role === 'admin') return 'admin';
        if (data?.role === 'customer') return 'customer';
      }
    } catch (err) {
      logger.warn('Firestore rol okunamadı, customer varsayılıyor:', err);
    }

    return 'customer';
  };

  // Init: Firebase Auth state subscription
  useEffect(() => {
    // Eski local-auth verilerini temizle (bir kerelik migration)
    try { LEGACY_KEYS.forEach(k => localStorage.removeItem(k)); } catch {}

    getRedirectResult(auth).catch(() => {/* ignore */});
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        const createdAt = fbUser.metadata?.creationTime || new Date().toISOString();
        const base = {
          uid: fbUser.uid,
          email: fbUser.email || '',
          displayName: fbUser.displayName || (fbUser.email ? fbUser.email.split('@')[0] : 'Kullanıcı'),
          createdAt,
        };

        const role = await determineRole(fbUser);

        const nextUser = { ...base, role } as User;
        setCurrentUser(nextUser);
        ensureUserDoc(fbUser.uid, base.email, role, createdAt);
        try {
          window.dispatchEvent(new CustomEvent('ensureCustomerProfile', { detail: {
            uid: nextUser.uid,
            fullName: nextUser.displayName,
            email: nextUser.email,
            createdAt: nextUser.createdAt,
          }}));
        } catch {}
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login: AuthContextType['login'] = async (email, password) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const u = cred.user;
      const createdAt = u.metadata?.creationTime || new Date().toISOString();
      const emailFinal = (u.email || email);
      const role = await determineRole(u);
      const nextUser: User = { uid: u.uid, email: emailFinal, displayName: u.displayName || emailFinal.split('@')[0], createdAt, role };
      setCurrentUser(nextUser);
      ensureUserDoc(u.uid, emailFinal, role, createdAt);
      try { window.dispatchEvent(new CustomEvent('ensureCustomerProfile', { detail: { uid: nextUser.uid, fullName: nextUser.displayName, email: nextUser.email, createdAt: nextUser.createdAt } })); } catch {}
      return { success: true };
    } catch (e: any) {
      const code = e?.code || '';
      let errorMessage = 'Giriş başarısız';
      switch (code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
          errorMessage = 'E-posta veya şifre hatalı. Lütfen kontrol edip tekrar deneyin.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Geçersiz e-posta adresi formatı.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Bu hesap devre dışı bırakılmış. Lütfen destek ile iletişime geçin.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Çok fazla başarısız giriş denemesi. Lütfen birkaç dakika bekleyip tekrar deneyin.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'İnternet bağlantınızı kontrol edin ve tekrar deneyin.';
          break;
        default:
          logger.error('Login hatası:', code, e?.message);
          errorMessage = e?.message || 'Giriş başarısız. Lütfen tekrar deneyin.';
      }
      return { success: false, error: errorMessage };
    }
  };

  const register: AuthContextType['register'] = async (email, password, profileData) => {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (profileData?.displayName) {
        await fbUpdateProfile(cred.user, { displayName: profileData.displayName });
      }
      const fbUser = cred.user;
      const createdAt = fbUser.metadata?.creationTime || new Date().toISOString();
      // Yerel profil (adres/şablonlar) için boş kayıt aç
      const profiles = readProfiles();
      profiles[fbUser.uid] = profiles[fbUser.uid] || { addresses: [], quickOrders: [] };
      const consentNow = new Date().toISOString();
      profiles[fbUser.uid].consents = {
        terms: { accepted: !!profileData?.termsConsent, date: consentNow },
        kvkk: { accepted: !!profileData?.kvkkConsent, date: consentNow },
        marketing: { accepted: !!profileData?.marketingConsent, date: consentNow },
      };
      writeProfiles(profiles);
      const emailFinal = fbUser.email || email;
      const role = await determineRole(fbUser);
      const regUser: User = { uid: fbUser.uid, email: emailFinal, displayName: fbUser.displayName || (profileData?.displayName || email.split('@')[0]), createdAt, role };
      setCurrentUser(regUser);
      try {
        await setDoc(doc(db, COLLECTIONS.USERS, fbUser.uid), {
          consents: {
            terms: { accepted: !!profileData?.termsConsent, date: consentNow },
            kvkk: { accepted: !!profileData?.kvkkConsent, date: consentNow },
            marketing: { accepted: !!profileData?.marketingConsent, date: consentNow },
          }
        }, { merge: true });
      } catch {}
      ensureUserDoc(fbUser.uid, emailFinal, role, createdAt);
      try { window.dispatchEvent(new CustomEvent('ensureCustomerProfile', { detail: { uid: regUser.uid, fullName: regUser.displayName, email: regUser.email, createdAt: regUser.createdAt } })); } catch {}
      return { success: true };
    } catch (e: any) {
      return { success: false, error: e?.message || 'Kayıt başarısız' };
    }
  };

  const loginWithGoogle: AuthContextType['loginWithGoogle'] = async () => {
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      const u = cred.user;
      const createdAt = u.metadata?.creationTime || new Date().toISOString();
      const profiles = readProfiles();
      profiles[u.uid] = profiles[u.uid] || { addresses: [], quickOrders: [] };
      writeProfiles(profiles);
      const emailFinal = u.email || '';
      const role = await determineRole(u);
      const gUser: User = { uid: u.uid, email: emailFinal, displayName: u.displayName || (u.email ? u.email.split('@')[0] : 'Kullanıcı'), createdAt, role };
      setCurrentUser(gUser);
      ensureUserDoc(u.uid, emailFinal, role, createdAt);
      try { window.dispatchEvent(new CustomEvent('ensureCustomerProfile', { detail: { uid: gUser.uid, fullName: gUser.displayName, email: gUser.email, createdAt: gUser.createdAt } })); } catch {}
      return { success: true };
    } catch (e: any) {
      const code = e?.code || '';
      if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
        try {
          await signInWithRedirect(auth, googleProvider);
          return { success: true };
        } catch (e2: any) {
          return { success: false, error: e2?.message || 'Google yönlendirmesi başarısız' };
        }
      }
      if (code === 'auth/operation-not-allowed') {
        return { success: false, error: 'Firebase Console → Authentication → Sign-in method bölümünde Google sağlayıcısını etkinleştirin.' };
      }
      if (code === 'auth/unauthorized-domain') {
        return { success: false, error: 'Firebase Console → Authentication → Settings → Authorized domains listesine localhost ve üretim alan adınızı ekleyin.' };
      }
      if (code === 'auth/invalid-api-key') {
        return { success: false, error: 'Geçersiz API anahtarı: .env içindeki REACT_APP_FIREBASE_API_KEY değerini kontrol edin.' };
      }
      return { success: false, error: e?.message || 'Google ile giriş başarısız' };
    }
  };

  const logout: AuthContextType['logout'] = async () => {
    try { await signOut(auth); } catch {}
    setCurrentUser(null);
    return { success: true };
  };

  const resetPassword: AuthContextType['resetPassword'] = async (email) => {
    try { await sendPasswordResetEmail(auth, email); return { success: true }; }
    catch (e: any) { return { success: false, error: e?.message || 'Şifre sıfırlama başarısız' }; }
  };

  // Profile helpers (addresses, quick orders) — localStorage cache
  const getProfile = (): UserProfile => {
    const uid = currentUser?.uid;
    if (!uid) return { addresses: [], quickOrders: [] };
    const profiles = readProfiles();
    return profiles[uid] || { addresses: [], quickOrders: [] };
  };
  const updateProfile = (updater: (p: UserProfile) => UserProfile) => {
    const uid = currentUser?.uid;
    if (!uid) return;
    const profiles = readProfiles();
    const next = updater(profiles[uid] || { addresses: [], quickOrders: [] });
    profiles[uid] = next;
    writeProfiles(profiles);
    try { window.dispatchEvent(new CustomEvent('authProfileChanged', { detail: { uid } })); } catch {}
  };

  // Addresses
  const getAddresses = () => getProfile().addresses;
  const addAddress: AuthContextType['addAddress'] = (address) => {
    const newAddr: Address = { id: 'a_' + Date.now().toString(36), isDefault: false, ...address };
    updateProfile((p) => {
      let addrs = [...p.addresses, newAddr];
      if (addrs.length === 1 || address.isDefault) {
        addrs = addrs.map(a => ({ ...a, isDefault: a.id === newAddr.id }));
      }
      return { ...p, addresses: addrs };
    });
    return newAddr;
  };
  const updateAddress: AuthContextType['updateAddress'] = (address) => {
    updateProfile((p) => {
      let addrs = p.addresses.map(a => a.id === address.id ? { ...address } : a);
      if (address.isDefault) {
        addrs = addrs.map(a => ({ ...a, isDefault: a.id === address.id }));
      }
      return { ...p, addresses: addrs };
    });
  };
  const deleteAddress: AuthContextType['deleteAddress'] = (id) => {
    updateProfile((p) => {
      const remaining = p.addresses.filter(a => a.id !== id);
      const hasDefault = remaining.some(a => a.isDefault);
      if (!hasDefault && remaining[0]) remaining[0].isDefault = true;
      return { ...p, addresses: remaining };
    });
  };
  const setDefaultAddress: AuthContextType['setDefaultAddress'] = (id) => {
    updateProfile((p) => ({ ...p, addresses: p.addresses.map(a => ({ ...a, isDefault: a.id === id })) }));
  };

  // Quick orders
  const getQuickOrders = () => getProfile().quickOrders;
  const saveQuickOrder: AuthContextType['saveQuickOrder'] = (template) => {
    const newT: QuickOrderTemplate = { id: 'q_' + Date.now().toString(36), createdAt: new Date().toISOString(), ...template };
    updateProfile((p) => ({ ...p, quickOrders: [newT, ...p.quickOrders] }));
    return newT;
  };
  const deleteQuickOrder: AuthContextType['deleteQuickOrder'] = (id) => {
    updateProfile((p) => ({ ...p, quickOrders: p.quickOrders.filter(q => q.id !== id) }));
  };

  const setFavoriteQuickOrder: AuthContextType['setFavoriteQuickOrder'] = (id) => {
    updateProfile((p) => ({
      ...p,
      quickOrders: p.quickOrders.map(q => ({ ...q, isFavorite: q.id === id })),
    }));
  };

  const setLastUsedAddress = (id: string) => {
    updateProfile((p) => ({ ...p, lastUsedAddressId: id }));
  };
  const getLastUsedAddress = () => getProfile().lastUsedAddressId;

  const updateEmail: AuthContextType['updateEmail'] = async (newEmail) => {
    const cur = currentUser;
    if (!cur) return { success: false, error: 'Oturum bulunamadı' };
    try {
      if (!auth.currentUser) return { success: false, error: 'Firebase kullanıcısı yok' };
      await fbUpdateEmail(auth.currentUser, newEmail);
      const next: User = { ...cur, email: newEmail };
      setCurrentUser(next);
      await ensureUserDoc(next.uid, newEmail, next.role || 'customer', next.createdAt);
      try { window.dispatchEvent(new CustomEvent('authProfileChanged', { detail: { uid: next.uid } })); } catch {}
      return { success: true };
    } catch (e: any) {
      const msg = e?.message || 'E-posta güncellenemedi';
      return { success: false, error: msg };
    }
  };

  const canEditEmail = useMemo(() => {
    try {
      const provs = auth.currentUser?.providerData || [];
      const isGoogle = provs.some(p => p.providerId === 'google.com');
      return !isGoogle; // Google ile girişte e-posta değiştirilemez
    } catch {
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo<AuthContextType>(() => ({
    currentUser,
    loading,
    login,
    register,
    loginWithGoogle,
    logout,
    resetPassword,
    getAddresses,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
    getQuickOrders,
    saveQuickOrder,
    deleteQuickOrder,
    setFavoriteQuickOrder,
    setLastUsedAddress,
    getLastUsedAddress,
    updateEmail,
    canEditEmail,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [currentUser, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
